// overview/useGroupData — derived-data-hooks för OverviewDialogens
// vyer: topologisk sortering av blocks inom en grupp + per-grupp-data.
// Egen fil (inte shared.tsx) för react-refresh: en fil ska exportera
// antingen bara komponenter eller bara icke-komponenter.

import { useMemo } from 'react';
import type { WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';

export function useTopologicalOrder(groupBlocks: WorkflowBlock[], internalConnectors: Connector[]) {
  return useMemo(() => {
    if (groupBlocks.length === 0) return [];
    const adjacency = new Map<string, string[]>();
    const inDegree = new Map<string, number>();
    groupBlocks.forEach(b => { adjacency.set(b.id, []); inDegree.set(b.id, 0); });
    internalConnectors.forEach(c => {
      adjacency.get(c.fromId)?.push(c.toId);
      inDegree.set(c.toId, (inDegree.get(c.toId) || 0) + 1);
    });
    const queue = groupBlocks.filter(b => (inDegree.get(b.id) || 0) === 0).map(b => b.id);
    const result: string[] = [];
    while (queue.length > 0) {
      const id = queue.shift()!;
      result.push(id);
      for (const next of adjacency.get(id) || []) {
        inDegree.set(next, (inDegree.get(next) || 0) - 1);
        if (inDegree.get(next) === 0) queue.push(next);
      }
    }
    groupBlocks.forEach(b => { if (!result.includes(b.id)) result.push(b.id); });
    return result;
  }, [groupBlocks, internalConnectors]);
}

export function useGroupData(group: BlockGroup, blocks: WorkflowBlock[], connectors: Connector[]) {
  const groupBlocks = useMemo(() => blocks.filter(b => group.blockIds.includes(b.id)), [blocks, group.blockIds]);
  const groupBlockIds = useMemo(() => new Set(group.blockIds), [group.blockIds]);
  const internalConnectors = useMemo(
    () => connectors.filter(c => groupBlockIds.has(c.fromId) && groupBlockIds.has(c.toId)),
    [connectors, groupBlockIds]
  );
  const ordered = useTopologicalOrder(groupBlocks, internalConnectors);
  const connectedPairs = useMemo(
    () => new Set(internalConnectors.map(c => `${c.fromId}->${c.toId}`)),
    [internalConnectors]
  );
  return { groupBlocks, internalConnectors, ordered, connectedPairs };
}
